/**
 * /faq — Preguntas frecuentes.
 *
 * Both a standalone page (linked from the footer and from inside the app
 * help menu) AND the data source for the FAQ section embedded in the
 * landing page. The questions live in `FAQ_ITEMS` so both views stay in
 * sync.
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { LegalShell } from '@/components/layout/LegalShell';
import { contact } from '@/lib/contact';

interface FaqItem {
  question: string;
  answer: React.ReactNode;
}

/** Master list of questions — exported so the landing page can reuse it. */
export const FAQ_ITEMS: FaqItem[] = [
  {
    question: '¿AlphaConcreto reemplaza a un ingeniero estructural?',
    answer: (
      <>
        No. Es una <strong>herramienta de cálculo y verificación</strong> que automatiza el diseño
        según la E.060 y el ACI 318, pero las decisiones finales — tipología, redundancia,
        ductilidad, detalle constructivo — siguen siendo responsabilidad del ingeniero proyectista
        colegiado.
      </>
    ),
  },
  {
    question: '¿Qué módulos puedo usar hoy?',
    answer: (
      <>
        Columnas, vigas, losas (aligeradas y macizas), muros estructurales (placas), zapatas
        (aislada, corrida, combinada, conectada) y predimensionamiento. Próximamente: auditoría
        E.060 / ACI, exportación BIM al ecosistema Alpha+.
      </>
    ),
  },
  {
    question: '¿Qué son los "fierros" y cómo funcionan?',
    answer: (
      <>
        Los <strong>fierros</strong> son créditos para exportar PDFs y Excels. Cada exportación
        cuesta 2 fierros; los predimensionamientos cuestan 1. Te regalamos 15 al registrarte y 15
        más cada mes. Si exportas mucho, puedes comprar packs (50, 150) o suscribirte para
        ilimitado.
      </>
    ),
  },
  {
    question: '¿Los fierros caducan?',
    answer: (
      <>
        Los fierros comprados <strong>no caducan</strong>. El bono mensual de 15 fierros no se
        acumula: si no usas los del mes, no se suman al siguiente. La idea es premiar la actividad.
      </>
    ),
  },
  {
    question: '¿Puedo cancelar mi suscripción cuando quiera?',
    answer: (
      <>
        Sí. Desde tu panel de Facturación puedes cancelar en cualquier momento. La suscripción
        seguirá activa hasta el final del periodo ya pagado y no se renovará automáticamente.
      </>
    ),
  },
  {
    question: '¿Qué pasa si se acaba mi suscripción?',
    answer: (
      <>
        Vuelves al modelo de fierros. Recibes tu bono mensual y todos los fierros que tenías
        antes (si compraste packs adicionales) siguen ahí. Las exportaciones simplemente vuelven a
        descontarse del saldo.
      </>
    ),
  },
  {
    question: '¿Aceptan Yape además de tarjeta?',
    answer: (
      <>
        Sí, los pagos se procesan por <strong>Culqi</strong>, que acepta Visa, MasterCard, American
        Express, Diners y <strong>Yape</strong>. No almacenamos tus datos de tarjeta en ningún
        momento.
      </>
    ),
  },
  {
    question: '¿Puedo exportar a Excel y no solo a PDF?',
    answer: (
      <>
        Sí. Cada cálculo se puede exportar como <strong>PDF</strong> (memoria de cálculo con
        diagramas) o <strong>XLSX</strong> (hoja editable con fórmulas). Ambas opciones consumen
        la misma cantidad de fierros.
      </>
    ),
  },
  {
    question: '¿Mis datos están seguros?',
    answer: (
      <>
        Sí. Contraseñas con hashing bcrypt, TLS en tránsito, JWT con expiración de 24 h, y nunca
        almacenamos datos de tarjeta. Lee más en nuestra{' '}
        <a href="/privacidad" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
          Política de Privacidad
        </a>
        .
      </>
    ),
  },
  {
    question: '¿Necesito instalar algo?',
    answer: (
      <>
        No. AlphaConcreto corre íntegramente en el navegador. Funciona en Chrome, Edge, Firefox y
        Safari modernos. Los diagramas 3D usan WebGL, así que un GPU integrado moderno es
        suficiente.
      </>
    ),
  },
  {
    question: '¿Cómo solicito un reembolso?',
    answer: (
      <>
        Escríbenos a{' '}
        <a href={`mailto:${contact.refundsEmail}`} className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
          {contact.refundsEmail}
        </a>{' '}
        dentro de los 7 días posteriores a la compra. Consulta los detalles en nuestra{' '}
        <a href="/reembolsos" className="underline-offset-2 hover:underline" style={{ color: 'var(--accent)' }}>
          Política de reembolsos
        </a>
        .
      </>
    ),
  },
];

export function FaqPage() {
  return (
    <LegalShell eyebrow="Soporte" title="Preguntas frecuentes" updated="Última actualización: mayo de 2026">
      <FaqList items={FAQ_ITEMS} />
    </LegalShell>
  );
}

/**
 * Accordion list — controlled. Exported so the landing page can render
 * the same UI inline without going through `LegalShell`.
 */
export function FaqList({ items }: { items: FaqItem[] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return (
    <div
      className="overflow-hidden rounded-[var(--radius-md)] border"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      {items.map((it, i) => {
        const open = openIdx === i;
        return (
          <div key={i} className={i > 0 ? 'border-t' : ''} style={{ borderColor: 'var(--border-subtle)' }}>
            <button
              type="button"
              onClick={() => setOpenIdx(open ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--surface-2)]"
              aria-expanded={open}
            >
              <span className="text-[14.5px] font-medium" style={{ color: 'var(--text-primary)' }}>
                {it.question}
              </span>
              <ChevronDown
                size={16}
                strokeWidth={2}
                className="shrink-0 transition-transform"
                style={{
                  color: 'var(--accent)',
                  transform: open ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </button>
            {open && (
              <div
                className="px-5 pb-4 text-[13.5px]"
                style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}
              >
                {it.answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
